/**
 * Calls for the camera connectivity check (ENG-2457). All go through
 * `createApi` like the rest of the app. These surfaces draw their own empty,
 * error and "not available yet" states, so — like the Troubleshooter — they
 * take the session-refresh wrapper rather than `universalAPIErrorHandler`
 * (whose generic alert and redirect would hide the specific answer: a test
 * already running, the hourly limit, the dry run not switched on yet).
 *
 * The endpoints ship in actuate_admin alongside this UI. Until they are on a
 * given environment, a 404/405 reads as "not available yet", never as a fault.
 */
import { AxiosError } from "axios";
import { createApi } from "../../../utils/createApi";
import { PERMISSION_DENIED_MESSAGES } from "../../../utils/universalAPIErrorHandler";
import { withSessionRecovery } from "../../Troubleshooting/TroubleshootingUtils/authRecovery";
import {
  ApiOutcome,
  ConnectivityResult,
  StartDryRunRequest,
  StartDryRunResponse,
} from "./siteConnectivityTypes";

export const siteConnectivityPath = (siteId: number) =>
  `site-connectivity/${siteId}/`;
export const DRY_RUN_PATH = "camera-dry-run/";
export const dryRunPath = (runId: string) =>
  `camera-dry-run/${encodeURIComponent(runId)}/`;
export const latestDryRunPath = (siteId: number) =>
  `camera-dry-run/latest/?site_id=${siteId}`;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

/**
 * When the next test is allowed, from a 429. admin may say so in the body
 * (an ISO time, or seconds to wait) or in a `Retry-After` header (seconds or
 * an HTTP date). `now` is injectable for tests.
 */
export const retryAtFrom = (
  error: AxiosError,
  now: number = Date.now()
): string | null => {
  const body = asRecord(error.response?.data);
  for (const key of ["retry_at", "next_allowed_at", "available_at"]) {
    const value = body[key];
    if (typeof value === "string" && !Number.isNaN(Date.parse(value))) {
      return new Date(value).toISOString();
    }
  }
  const seconds = (value: unknown): number | null => {
    const n = typeof value === "string" ? Number(value) : value;
    return typeof n === "number" && Number.isFinite(n) && n >= 0 ? n : null;
  };
  const bodySeconds =
    seconds(body.retry_after_seconds) ?? seconds(body.retry_after);
  if (bodySeconds != null) {
    return new Date(now + bodySeconds * 1000).toISOString();
  }
  const headers = asRecord(error.response?.headers);
  const header = headers["retry-after"] ?? headers["Retry-After"];
  const headerSeconds = seconds(header);
  if (headerSeconds != null) {
    return new Date(now + headerSeconds * 1000).toISOString();
  }
  if (typeof header === "string" && !Number.isNaN(Date.parse(header))) {
    return new Date(header).toISOString();
  }
  return null;
};

/** What admin's 400 codes mean, for the person who pressed the button. */
export const REJECTED_MESSAGES: Record<string, string> = {
  no_cameras:
    "There are no cameras at this site to test yet. Add cameras, then run the test.",
  too_many_cameras:
    "This site has more than 200 cameras, too many to test at once. Test a subset from each camera page.",
};

/** Turn a failed request into the outcome the UI explains. */
export const outcomeFromError = <T>(error: unknown): ApiOutcome<T> => {
  const axiosError = error as AxiosError;
  const status = axiosError?.response?.status;
  const body = asRecord(axiosError?.response?.data);
  if (status === 404) return { kind: "not_found" };
  if (status === 405 || status === 501 || status === 503) {
    return { kind: "unavailable" };
  }
  if (status === 409) {
    return {
      kind: "busy",
      runId: typeof body.run_id === "string" ? body.run_id : null,
    };
  }
  if (status === 429) {
    // `cluster_busy`: our test capacity is full, not this site's hourly limit.
    return body.code === "cluster_busy"
      ? { kind: "cluster_busy", retryAt: retryAtFrom(axiosError) }
      : { kind: "rate_limited", retryAt: retryAtFrom(axiosError) };
  }
  if (status === 400) {
    if (body.code === "not_supported") return { kind: "not_supported" };
    const message =
      typeof body.code === "string" && REJECTED_MESSAGES[body.code];
    if (message) return { kind: "rejected", code: String(body.code), message };
    return {
      kind: "error",
      message:
        typeof body.detail === "string" && body.detail
          ? body.detail
          : "The server couldn't start the test with these settings.",
    };
  }
  if (status === 401 || status === 403) {
    const detail = typeof body.detail === "string" ? body.detail : "";
    return {
      kind: "forbidden",
      message:
        PERMISSION_DENIED_MESSAGES[detail] ||
        "You don't have permission to test cameras at this site. Ask your administrator if you need access.",
    };
  }
  if (status != null && status >= 500) {
    return {
      kind: "error",
      message: `The server couldn't answer (error ${status}). Try again in a minute.`,
    };
  }
  return {
    kind: "error",
    message:
      "We couldn't reach the server. Check your internet connection and try again.",
  };
};

/** A body that says the dry run isn't switched on, even with a 2xx. */
const saysUnavailable = (data: unknown): boolean =>
  asRecord(data).status === "unavailable";

const run = async <T>(
  request: () => Promise<{ data: T }>
): Promise<ApiOutcome<T>> => {
  try {
    const response = await withSessionRecovery(request);
    if (saysUnavailable(response.data)) return { kind: "unavailable" };
    return { kind: "ok", data: response.data };
  } catch (error) {
    return outcomeFromError<T>(error);
  }
};

/**
 * The connector writes its internal deployment name into its messages
 * ("No frame returned for connector-52435 - Front gate"). Customers know the
 * camera by its name, not our deployment id: drop the id where a camera name
 * follows it, and say "the connector" anywhere else.
 */
const CONNECTOR_ID = /\bconnector-\d+(?:\/preview)?\b/g;
export const plainDetail = <T extends string | null | undefined>(text: T): T =>
  (typeof text === "string"
    ? text
        .replace(/\bconnector-\d+(?:\/preview)?\s+[-–—]\s+/g, "")
        .replace(CONNECTOR_ID, "the connector")
    : text) as T;

const plainResult = (
  outcome: ApiOutcome<ConnectivityResult>
): ApiOutcome<ConnectivityResult> =>
  outcome.kind === "ok"
    ? {
        ...outcome,
        data: {
          ...outcome.data,
          detail: plainDetail(outcome.data.detail),
          error: plainDetail(outcome.data.error),
          cameras: outcome.data.cameras?.map((camera) => ({
            ...camera,
            detail: plainDetail(camera.detail),
          })),
        },
      }
    : outcome;

/** What the running connector last reported for each camera at the site. */
export const fetchSiteConnectivity = async (
  siteId: number
): Promise<ApiOutcome<ConnectivityResult>> => {
  const outcome = await run(() =>
    createApi("").get<ConnectivityResult>(siteConnectivityPath(siteId))
  );
  // A 404 here means the route isn't deployed (a site the user can't see is
  // a 403), so it reads as "not available yet" rather than "no data".
  return outcome.kind === "not_found"
    ? { kind: "unavailable" }
    : plainResult(outcome);
};

/** Start a full camera test. 202 → `{run_id, status: "pending"}`. */
export const startCameraDryRun = async (
  request: StartDryRunRequest
): Promise<ApiOutcome<StartDryRunResponse>> => {
  const outcome = await run(() =>
    createApi("").post<StartDryRunResponse>(DRY_RUN_PATH, request)
  );
  // POST to a route that doesn't exist yet is a 404: not available yet.
  return outcome.kind === "not_found" ? { kind: "unavailable" } : outcome;
};

export const fetchCameraDryRun = async (
  runId: string
): Promise<ApiOutcome<ConnectivityResult>> =>
  plainResult(
    await run(() => createApi("").get<ConnectivityResult>(dryRunPath(runId)))
  );

/** The site's most recent test; `not_found` when it has never had one. */
export const fetchLatestCameraDryRun = async (
  siteId: number
): Promise<ApiOutcome<ConnectivityResult>> =>
  plainResult(
    await run(() =>
      createApi("").get<ConnectivityResult>(latestDryRunPath(siteId))
    )
  );
